/**
 * Remote pools for the command palette (OPSFL-POLISH-1) — the flow estate and the entity browser,
 * mapped onto palette rows. Kept out of the host component so the caches are module-scoped (they
 * must survive a re-render) and so the host stays a component-only module.
 *
 * Both sources swallow their errors on purpose: a palette that pops an API error while the
 * operator is trying to LEAVE the broken page is worse than a palette that quietly offers only
 * navigation. The failing surface reports its own error state; the palette is the way out.
 */
import { listEntities } from '@/api/entities';
import { listFlows } from '@/api/flows';
import type { PaletteItem } from './paletteModel';

/**
 * `GET /v1/flows` has no server-side search, so the estate is pulled ONCE per 60 s and ranked
 * locally: one request per palette session instead of one per keystroke. That is the throttle —
 * typing costs nothing, the network sees at most one call a minute.
 */
const FLOW_TTL_MS = 60_000;
let flowCache: { at: number; items: PaletteItem[] } | null = null;

export async function loadFlowItems(): Promise<PaletteItem[]> {
  if (flowCache && Date.now() - flowCache.at < FLOW_TTL_MS) return flowCache.items;
  try {
    const flows = await listFlows();
    const items = flows.map<PaletteItem>((f) => ({
      id: `flow:${f.id}`,
      kind: 'flow',
      label: f.name || f.id,
      hint: f.publishedVersion
        ? `published v${f.publishedVersion}`
        : `draft · latest v${f.latestVersion}`,
      keywords: f.id,
      route: `/flows/${encodeURIComponent(f.id)}`,
      meta: f.id,
    }));
    flowCache = { at: Date.now(), items };
    return items;
  } catch {
    return [];
  }
}

/** Entity ids are server-searched by prefix — the estate is far too large to hold client-side. */
export async function loadEntityItems(query: string): Promise<PaletteItem[]> {
  const q = query.trim();
  if (!q) return [];
  try {
    const page = await listEntities({ idPrefix: q, pageSize: 8 });
    return page.items.map<PaletteItem>((e) => ({
      id: `entity:${e.id}`,
      kind: 'entity',
      label: e.id,
      hint: `${e.typeKey} · ${e.status}`,
      route: `/entities/${encodeURIComponent(e.id)}`,
    }));
  } catch {
    return [];
  }
}

/** Test seam / sign-out hygiene: drop the cached estate. */
export function resetPaletteCaches(): void {
  flowCache = null;
}
